import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useServiceToken } from '@/auth/JwtAuthenticator/JwtAuthenticator';
import { useAppConfigContext } from '@/context/AppConfig';
import { request } from '@/utils/index';

const queryKey = 'questions';

interface Answer {
  screen_id: string;
  custom_attribute: string;
  response?: string;
}

class Bowtie {
  /* You can use getQuestions with or without a plan id. By default (with no plan id),
  it returns the default question set for the brand */
  public static async getQuestions(token: string, planId?: string) {
    let url = '/bow/v1/question_set';
    if (planId) {
      const queryParams = new URLSearchParams({
        plan_id: planId || '',
      });
      url += `?${queryParams}`;
    }
    const response = await fetch(url, {
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`status ${response.status}`);
    }

    return await response.json();
  }

  public static async recordAnswers(
    token: string,
    planId: string,
    answers: Answer[]
  ) {
    return await request('/bow/v1/answers', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        plan_id: planId,
        answers,
      }),
    });
  }
}

export function useBowtie(planId?: string) {
  const queryClient = useQueryClient();
  const { logError } = useAppConfigContext();
  const { data: tokenData } = useServiceToken('bow_tie');

  const questions = useQuery({
    queryKey: [queryKey, planId],
    queryFn: () => Bowtie.getQuestions(tokenData?.token, planId),

    onError: (error) => {
      logError('Error fetching questions', error as Error);
    },
    enabled: !!tokenData?.token,
    staleTime: 5 * 60 * 1000,
    cacheTime: 6 * 60 * 1000,
  });

  const recordAnswers = useMutation({
    mutationFn: ({ id, answers }: { id: string; answers: Answer[] }) => {
      return Bowtie.recordAnswers(tokenData?.token, id, answers);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
      });
    },
    onError: (error) => {
      logError('Error saving answers', error as Error);
    },
  });

  return {
    questions,
    recordAnswers,
  };
}
